import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'wouter'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import {
	AlertCircle,
	Check,
	ChevronRight,
	Home,
	Clock,
	CreditCard,
	ChevronLeft,
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Header from '../components/Header'
import { apiFetch } from '@/utils'
import { useUser } from '@/hooks/use-user'
import { useGetActiveMemberships } from '@/hooks/use-getActiveMemberships'
import { getSignedTransactionData } from '@/utils/transactions'
import { useGetActiveEntryPass } from '@/hooks/use-getActiveEntryPass'
import { usePrices } from '@/hooks/use-prices'
import { mutate } from 'swr'
import PaymentConfirmationDialog from '@/components/PaymentConfirmationDialog'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import AvailableEntryPassCard from '@/components/AvailableEntryPassCard'

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

interface PassOption {
	id: string
	name: string
	price: number
	startTime: string
	endTime: string
	description: string
	features: string[]
}

interface EnhancedPassOption extends PassOption {
	isAvailable: boolean
	usdPrice: number
	sechPrice: number
	taxCalculation?: TaxCalculationResult
	totalSechPrice?: number
}

const PASS_OPTIONS: PassOption[] = [
	{
		id: 'day',
		name: 'Secrets Day Pass',
		price: 25,
		startTime: '10:00',
		endTime: '18:00',
		description: 'Perfect for daytime activities',
		features: [
			'Full club access during day hours',
			'Access to all facilities',
			'Complimentary refreshments'
		]
	},
	{
		id: 'night',
		name: 'Secrets Night Pass',
		price: 35,
		startTime: '20:00',
		endTime: '04:00',
		description: 'Experience the nightlife',
		features: [
			'Full club access during night hours',
			'Special evening events access',
			'VIP area entry',
			'Complimentary welcome drink'
		]
	},
	{
		id: 'special',
		name: 'Secrets Special Event Pass',
		price: 50,
		startTime: '00:00',
		endTime: '23:59',
		description: 'Access to exclusive special events',
		features: [
			'Access to themed parties',
			'Special event exclusive areas',
			'Dedicated host service',
			'Event merchandise',
			'Priority entry'
		]
	}
]

function isPassAvailable(startTime: string, endTime: string): boolean {
	const now = new Date()
	const [startHour, startMinute] = startTime.split(':').map(Number)
	const [endHour, endMinute] = endTime.split(':').map(Number)

	const currentTime = now.getHours() * 60 + now.getMinutes()
	const start = startHour * 60 + startMinute
	let end = endHour * 60 + endMinute

	if(end < start) {
		end += 24 * 60
		if(currentTime < start) {
			return currentTime + 24 * 60 >= start && currentTime + 24 * 60 <= end
		}
	}

	return currentTime >= start && currentTime <= end
}

export default function EntryPassesPage() {
	const [isProcessing, setIsProcessing] = useState(false)
	const [selectedPass, setSelectedPass] = useState<string | null>(null)
	const [validationError, setValidationError] = useState<string | null>(null)
	const [isValidating, setIsValidating] = useState(true)
	const [taxCalculations, setTaxCalculations] = useState<{ [key: string]: TaxCalculationResult }>({})
	const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false)
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const [currentTime, setCurrentTime] = useState(new Date())

	const { uid, isConnected, walletAddress, loading, connectWallet } = useUser()
	const { convertUsdToToken } = usePrices()
	const {
		activeMemberships,
		isLoading: isMembershipLoading,
		hasActiveMemberships,
		hasAllMembershipsExpired
	} = useGetActiveMemberships(walletAddress)
	const {
		activeEntryPass: currentEntryPass,
		isEntryPassLoading: isCurrentEntryPassLoading
	} = useGetActiveEntryPass(walletAddress)

	const [isConfirmingPassPurchase, setIsConfirmingPassPurchase] =
		useState(false)

	// Update time every minute
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentTime(new Date())
		}, 60000)

		return () => clearInterval(timer)
	}, [])

	// Enhanced passes with SECH pricing and tax calculations
	const passesWithSechPricing = useMemo(() => {
		return PASS_OPTIONS.map(pass => {
			const usdPrice = pass.price
			const sechPrice = convertUsdToToken(usdPrice, 'sech')
			const taxCalculation = taxCalculations[pass.id]
			const totalSechPrice = taxCalculation ? convertUsdToToken(taxCalculation.total, 'sech') : sechPrice
			const isAvailable = isPassAvailable(pass.startTime, pass.endTime)

			return {
				...pass,
				isAvailable,
				usdPrice,
				sechPrice,
				taxCalculation,
				totalSechPrice
			} as EnhancedPassOption
		})
	}, [convertUsdToToken, taxCalculations])

	// Calculate taxes for all passes
	useEffect(() => {
		const calculateTaxes = async () => {
			setIsCalculatingTaxes(true)
			const calculations: { [key: string]: TaxCalculationResult } = {}

			try {
				// Calculate taxes for all passes in parallel
				const taxPromises = PASS_OPTIONS.map(async (pass) => {
					try {
						const response = await apiFetch('/v1/taxes/calculate', {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify({
								amount: pass.price,
								transactionType: 'entry_pass'
							})
						})

						if(!response.ok) {
							throw new Error(`HTTP error! status: ${response.status}`)
						}

						const result = await response.json()
						if(result.success) {
							return { id: pass.id, data: result.data }
						} else {
							throw new Error(result.message || 'Tax calculation failed')
						}
					} catch(error) {
						console.error(`Failed to calculate taxes for pass ${pass.id}:`, error)
						// Return fallback calculation without taxes
						return {
							id: pass.id,
							data: {
								subtotal: pass.price,
								fees: [],
								total: pass.price
							}
						}
					}
				})

				const results = await Promise.all(taxPromises)

				results.forEach(result => {
					if(result) {
						calculations[result.id] = result.data
					}
				})

			} catch(error) {
				console.error('Error calculating taxes:', error)
				toast({
					title: 'Tax Calculation Error',
					description: 'Unable to calculate taxes. Showing base prices.',
					variant: 'destructive'
				})

				// Create fallback calculations
				PASS_OPTIONS.forEach(pass => {
					calculations[pass.id] = {
						subtotal: pass.price,
						fees: [],
						total: pass.price
					}
				})
			} finally {
				setTaxCalculations(calculations)
				setIsCalculatingTaxes(false)
			}
		}

		calculateTaxes()
	}, [toast])

	const selectedPassPrice = useMemo(() => {
		const pass = passesWithSechPricing.find((p) => p.id === selectedPass)
		return pass?.totalSechPrice || 0
	}, [passesWithSechPricing, selectedPass])

	// Enhanced validation for direct access
	useEffect(() => {
		const validateAccess = async () => {
			setIsValidating(true)
			setValidationError(null)
			if(isCurrentEntryPassLoading || isMembershipLoading || loading) return

			try {
				// Check membership
				console.log('-------->>>>>>>>>> ', { activeMemberships })
				if(!hasActiveMemberships) {
					setValidationError(
						'Please select a membership plan before accessing passes.'
					)
					setTimeout(() => navigate('/membership'), 3000)
					return
				}

				if(hasAllMembershipsExpired) {
					// 1 hour expiry
					localStorage.removeItem('selectedMembership')
					setValidationError(
						'Your membership selection has expired. Please select a plan again.'
					)
					setTimeout(() => navigate('/membership'), 3000)
					return
				}
				console.log('------>>>>>>>> After expiration check')

				// Check waivers if address is available
				if(walletAddress) {
					const response = await apiFetch(`/v1/waivers/my`, {
						withCredentials: true
					})
					if(!response.ok) {
						throw new Error('Failed to verify waiver status')
					}

					console.log('--->>>>>>>>. Waiver response', response)
					const waiverStatus = await response.json()
					console.log('--->>>>>>>>. Waiver status: ', waiverStatus)

					if(!waiverStatus.completed) {
						setValidationError(
							'Please complete both required waivers before accessing passes.'
						)
						console.log('===========))))>>>>>> Waiver not completed')
						setTimeout(() => navigate('/waivers'), 3000)
						return
					}
				}
			} catch(error) {
				console.error('Validation error:', error)
				setValidationError(
					'An error occurred while validating your access. Please try again.'
				)
				setTimeout(() => navigate('/membership'), 3000)
			} finally {
				setIsValidating(false)
			}
		}

		validateAccess()
	}, [
		navigate,
		loading,
		walletAddress,
		hasAllMembershipsExpired,
		isMembershipLoading,
		activeMemberships,
		isCurrentEntryPassLoading
	])

	const handlePurchase = async (passId: string) => {
		setIsConfirmingPassPurchase(false)

		const pass = passesWithSechPricing.find((p) => p.id === passId)
		if(!pass || !pass.isAvailable) {
			toast({
				title: 'Pass Unavailable',
				description: 'This pass is not available at the current time.',
				variant: 'destructive'
			})
			return
		}

		// Ensure we have tax calculation data
		if(!pass.taxCalculation) {
			toast({
				title: 'Tax Calculation Error',
				description: 'Tax calculation not available. Please try again.',
				variant: 'destructive'
			})
			return
		}

		setIsProcessing(true)
		try {
			// Show processing toast
			toast({
				title: 'Processing Payment',
				description: `Processing your ${pass.name} purchase...`
			})

			if(!walletAddress) {
				await connectWallet()
				if(!walletAddress) {
					throw new Error('Please connect your wallet to continue')
				}
			}

			let signature
			let nonce
			let forwardRequest
			// When user connects his wallet we should request signature to be able to finish transaction
			// in case with custodial wallet backend will handle it
			if(isConnected) {
				const {
					signedTx,
					txNonce,
					transferRequest: signatureData
				} = await getSignedTransactionData({
					userAddress: walletAddress,
					amount: pass.totalSechPrice?.toString() || pass.sechPrice.toString()
				})
				signature = signedTx
				nonce = txNonce
				forwardRequest = signatureData
			}

			console.log({
				signature,
				userAddress: walletAddress,
				nonce,
				pass
			})

			// Record entry pass in database - backend handles pricing and tax calculations
			const response = await apiFetch('/v1/entry-passes', {
				method: 'POST',
				body: JSON.stringify({
					userUid: uid,
					customerAddress: walletAddress,
					type: pass.id, // Only send the pass type ID, backend calculates price and taxes
					signature,
					nonce,
					forwardRequest
				})
			})

			if(!response.ok) {
				const errorData = await response.json()
				throw new Error(errorData.message || 'Failed to record entry pass')
			}

			// Success! Refresh data and reset state instead of page reload
			localStorage.removeItem('selectedMembership')

			// Clear selected pass to reset UI state
			setSelectedPass(null)

			// Refresh all data
			await mutate(`/v1/transactions/${walletAddress}`)
			await mutate(`/v1/entry-passes/${walletAddress}`)

			// Show success feedback
			toast({
				title: 'Success!',
				description: `Your ${pass.name} has been activated.`
			})

		} catch(error) {
			toast({
				title: 'Purchase Failed',
				description:
					error instanceof Error ? error.message : 'Failed to purchase pass',
				variant: 'destructive'
			})
		} finally {
			setIsProcessing(false)
		}
	}

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(IS_TESTNET ? 6 : 2)
	}

	const TaxBreakdownCard = ({ pass }: { pass: EnhancedPassOption }) => {
		if(!pass.taxCalculation) {
			return (
				<Card className="mt-4 p-4 bg-[#3C3C3C] border-0">
					<div className="flex items-center justify-center py-4">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
						<span className="ml-2 text-sm text-[#BABABB]">Calculating taxes...</span>
					</div>
				</Card>
			)
		}

		const { taxCalculation } = pass

		return (
			<Card className="mt-4 p-4 bg-[#3C3C3C] border-0">
				<h4 className="font-semibold mb-2 flex items-center text-[#ffffff]">
					<Check className="h-4 w-4 mr-2 text-green-500"/>
					Price Breakdown
				</h4>
				<div className="space-y-2 text-sm">
					<div className="flex justify-between">
						<span className="text-[#BABABB]">Subtotal:</span>
						<div className="text-right">
							<div className="text-[#ffffff]">${formatPrice(taxCalculation.subtotal)} USD</div>
							<div className="text-xs text-[#BABABB]">
								{formatPrice(convertUsdToToken(taxCalculation.subtotal, 'sech'))} SECH
							</div>
						</div>
					</div>
					{taxCalculation.fees.map((fee, index) => (
						<div key={index} className="flex justify-between text-[#BABABB]">
							<span>{fee.description} ({(fee.rate * 100).toFixed(1)}%):</span>
							<div className="text-right">
								<div>${formatPrice(fee.amount)} USD</div>
								<div className="text-xs">
									{formatPrice(convertUsdToToken(fee.amount, 'sech'))} SECH
								</div>
							</div>
						</div>
					))}
					<hr className="my-2 border-[#4C4C4C]"/>
					<div className="flex justify-between font-semibold">
						<span className="text-[#ffffff]">Total:</span>
						<div className="text-right">
							<div className="text-[#ffffff]">${formatPrice(taxCalculation.total)} USD</div>
							<div className="text-sm text-primary font-semibold">
								{formatPrice(pass.totalSechPrice || 0)} SECH
							</div>
						</div>
					</div>
				</div>
			</Card>
		)
	}

	if(isValidating) {
		return (
			<div className="min-h-screen bg-[#18181B]">
				<div className="px-5 py-4 flex items-center justify-center min-h-[60vh]">
					<Card className="p-6 text-center bg-[#3C3C3C] border-0">
						<Clock className="h-8 w-8 animate-spin mx-auto mb-4 text-primary"/>
						<h2 className="text-lg font-semibold mb-2 text-white">Validating Access</h2>
						<p className="text-sm text-[#BABABB]">
							Please wait while we verify your membership and waivers...
						</p>
					</Card>
				</div>
			</div>
		)
	}

	if(validationError) {
		return (
			<div className="min-h-screen bg-[#18181B]">
				<div className="px-5 py-4 flex items-center justify-center min-h-[60vh]">
					<Alert variant="destructive" className="max-w-lg">
						<AlertCircle className="h-4 w-4"/>
						<AlertDescription className="flex flex-col gap-2">
							<p>{validationError}</p>
							<p className="text-sm">Redirecting you shortly...</p>
						</AlertDescription>
					</Alert>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				<div
					role="banner"
					className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="absolute left-0
                        h-8 w-8
                        flex items-center justify-center
                        rounded-full
                        bg-white/10 hover:bg-white/20
                        focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]'/>
					</button>

					<h1 className="text-base font-semibold tracking-wide">
						Entry Passes
					</h1>
				</div>

				<div className="text-start mb-4">
					<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">Choose Your Pass</h1>
					<p className="text-[13px] leading-[15px] font-regular text-[#BABABB]">
						Select a pass that matches your schedule. All prices include applicable taxes.
					</p>
					<div className="mt-3 flex items-center text-sm text-[#BABABB]">
						<Clock className="h-4 w-4 mr-2"/>
						<span>Current time: {currentTime.toLocaleTimeString()}</span>
					</div>
					{isCalculatingTaxes && (
						<p className="text-primary mt-2 flex items-center gap-2">
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
							Calculating taxes for all passes...
						</p>
					)}
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
					{passesWithSechPricing.map((pass) => (
						<div key={pass.id} className="flex flex-col">
							<AvailableEntryPassCard
								pass={pass}
								isSelected={selectedPass === pass.id}
								isCurrent={pass.id === currentEntryPass?.type}
								canBuyToday={!isProcessing} // Disable during processing
								isLoading={isCalculatingTaxes}
								isProcessing={isProcessing && selectedPass === pass.id}
								onSelect={(clickedPass) => {
									if(isCalculatingTaxes || !clickedPass.taxCalculation || isProcessing) {
										return
									}
									if(selectedPass === clickedPass.id) {
										// Already selected, show confirmation
										setIsConfirmingPassPurchase(true)
									} else {
										// Select this pass
										setSelectedPass(clickedPass.id)
									}
								}}
							/>
							{selectedPass === pass.id && <TaxBreakdownCard pass={pass}/>}
						</div>
					))}
				</div>
			</div>
			<PaymentConfirmationDialog
				open={isConfirmingPassPurchase}
				amount={selectedPassPrice}
				onConfirm={() => handlePurchase(selectedPass!)}
				onCancel={() => setIsConfirmingPassPurchase(false)}
			/>
		</div>
	)
}